import { useState } from 'react';
import { Link } from 'react-router-dom';
import { usePageMeta } from '@/hooks/usePageMeta';
import Breadcrumb from '@/components/feature/Breadcrumb';

const container = 'w-full max-w-[1280px] mx-auto px-4 md:px-8';
const FORM_ADDR = 'https://readdy.ai/api/form/db8g15bk13ra8mig8r60';

const features = [
  { icon: 'ri-newspaper-line', title: '娱乐资讯', desc: '影视、综艺、八卦与音乐等多个频道，热点及时更新。' },
  { icon: 'ri-film-line', title: '视频资源', desc: '影视剧、综艺与短视频集中呈现，分类筛选一目了然。' },
  { icon: 'ri-fire-line', title: '热点专题', desc: '围绕热门话题聚合资讯与视频，一次看够来龙去脉。' },
  { icon: 'ri-chat-smile-2-line', title: '互动内容', desc: '支持搜索、筛选与分享，快速找到你的兴趣所在。' },
];

const notices = [
  '本站视频播放为前端模拟演示，播放与进度控制暂不具备真实功能，待后端数据接口接入后启用。',
  '全站搜索与分类筛选基于前端演示数据完成，用于展示交互效果。',
  '站内资讯内容均为演示内容，仅用于产品功能与视觉展示，不构成任何真实性陈述。',
  '我们重视你的隐私，反馈表单仅收集你主动填写的信息，不会用于其他用途。',
];

export default function AboutPage() {
  usePageMeta(
    '关于我们 - 91.con 多元娱乐资讯与内容平台',
    '了解 91.con 的品牌定位、内容服务与联系方式，欢迎通过反馈表单与我们交流建议与合作。',
    '关于我们,91.con,联系方式,娱乐资讯,视频资源,意见反馈'
  );

  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('website_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      form.reset();
      setMessage('');
      return;
    }

    setStatus('submitting');
    setFormError('');

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'website_alt') return;
      payload.append(key, String(value));
    });

    try {
      const response = await fetch(FORM_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');

      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setStatus('success');
        setMessage('');
        form.reset();
      } else {
        setStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试');
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，请稍后重试');
    }
  };

  return (
    <>
      <section className="w-full bg-background-100 border-b border-background-200">
        <div className={`${container} py-8 md:py-10`}>
          <Breadcrumb items={[{ label: '关于我们' }]} />
          <h1 className="font-heading text-2xl md:text-4xl font-extrabold text-foreground-950 mt-4">关于 91.con</h1>
          <p className="text-sm md:text-base text-foreground-600 mt-3 max-w-3xl leading-relaxed">
            91.con 是一个以「多元娱乐资讯与内容平台」为主题的综合娱乐网站，面向关注影视剧、综艺、热点新闻与娱乐话题的用户群体。
          </p>
        </div>
      </section>

      <section className={`${container} py-10 md:py-12`}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-10">
          <div className="lg:col-span-2 flex flex-col gap-10">
            <div>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 flex items-center gap-2.5 mb-4">
                <span className="w-1 h-6 rounded-full bg-primary-500" />
                网站定位
              </h2>
              <div className="flex flex-col gap-4 text-base text-foreground-700 leading-loose">
                <p>
                  我们聚合娱乐资讯新闻、影视综艺视频资源与热点专题内容，帮助用户在碎片化时间里快速了解娱乐热点信息、观看精选视频，并参与话题互动。
                </p>
                <p>
                  网站兼顾新闻的时效性与视频内容的易查找性，通过清晰的分类、筛选与搜索能力，让内容探索更加高效、阅读体验更加舒适。
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {features.map((f) => (
                <div key={f.title} className="p-5 rounded-lg bg-background-100 border border-background-200">
                  <span className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-500/15 text-primary-400">
                    <i className={`${f.icon} text-xl`} />
                  </span>
                  <h3 className="font-heading text-base font-bold text-foreground-950 mt-3">{f.title}</h3>
                  <p className="text-sm text-foreground-600 mt-1.5 leading-relaxed">{f.desc}</p>
                </div>
              ))}
            </div>

            <div>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 flex items-center gap-2.5 mb-4">
                <span className="w-1 h-6 rounded-full bg-accent-500" />
                使用须知与隐私提示
              </h2>
              <ul className="flex flex-col gap-3">
                {notices.map((n) => (
                  <li key={n} className="flex items-start gap-2.5 text-sm text-foreground-700 leading-relaxed">
                    <i className="ri-checkbox-circle-line text-accent-500 mt-0.5 shrink-0" />
                    {n}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <aside className="flex flex-col gap-6">
            <div className="p-5 rounded-lg bg-background-100 border border-background-200">
              <h2 className="font-heading text-base font-bold text-foreground-950 mb-4 flex items-center gap-2">
                <i className="ri-contacts-line text-primary-400" />
                联系方式
              </h2>
              <ul className="flex flex-col gap-3.5 text-sm text-foreground-700">
                <li className="flex items-start gap-2.5">
                  <i className="ri-mail-line text-primary-400 mt-0.5" />
                  <span>内容与反馈：contact@91con.com</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <i className="ri-briefcase-line text-primary-400 mt-0.5" />
                  <span>商务合作：biz@91con.com</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <i className="ri-time-line text-primary-400 mt-0.5" />
                  <span>服务时间：工作日 10:00 - 19:00</span>
                </li>
              </ul>
            </div>

            <div className="p-5 rounded-lg bg-accent-100/60 border border-accent-300/50">
              <h2 className="font-heading text-base font-bold text-accent-900 mb-2 flex items-center gap-2">
                <i className="ri-feedback-line" />
                意见反馈
              </h2>
              <p className="text-sm text-accent-900/80 leading-relaxed">
                有内容建议、纠错或合作意向？填写右侧表单告诉我们，我们会尽快查看。
              </p>
            </div>
          </aside>
        </div>

        <div className="mt-12 max-w-3xl">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 flex items-center gap-2.5 mb-2">
            <span className="w-1 h-6 rounded-full bg-primary-500" />
            反馈与联系
          </h2>
          <p className="text-sm text-foreground-600 mb-6">请填写以下信息，带 * 为必填项。</p>

          <form
            id="feedback-form"
            data-readdy-form
            action={FORM_ADDR}
            method="post"
            onSubmit={handleSubmit}
            className="p-5 md:p-7 rounded-lg bg-background-100 border border-background-200 flex flex-col gap-5"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="flex flex-col gap-2">
                <label htmlFor="fb-name" className="text-sm text-foreground-800">昵称 *</label>
                <input
                  id="fb-name"
                  name="name"
                  type="text"
                  required
                  placeholder="请输入你的昵称"
                  className="h-11 px-3.5 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-950 placeholder:text-foreground-500 outline-none focus:border-primary-500 transition-colors"
                />
              </div>
              <div className="flex flex-col gap-2">
                <label htmlFor="fb-email" className="text-sm text-foreground-800">邮箱 *</label>
                <input
                  id="fb-email"
                  name="email"
                  type="email"
                  required
                  placeholder="用于接收我们的回复"
                  className="h-11 px-3.5 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-950 placeholder:text-foreground-500 outline-none focus:border-primary-500 transition-colors"
                />
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="fb-topic" className="text-sm text-foreground-800">反馈类型</label>
              <select
                id="fb-topic"
                name="topic"
                defaultValue="内容建议"
                className="h-11 px-3.5 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-950 outline-none focus:border-primary-500 transition-colors cursor-pointer"
              >
                <option value="内容建议">内容建议</option>
                <option value="内容纠错">内容纠错</option>
                <option value="合作洽谈">合作洽谈</option>
                <option value="其他问题">其他问题</option>
              </select>
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="fb-message" className="text-sm text-foreground-800">留言内容 *</label>
              <textarea
                id="fb-message"
                name="message"
                required
                maxLength={500}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="请填写你的建议或问题（最多 500 字）"
                rows={5}
                className="px-3.5 py-3 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-950 placeholder:text-foreground-500 outline-none focus:border-primary-500 transition-colors resize-none"
              />
              <span className="text-xs text-foreground-500 text-right">{message.length} / 500</span>
            </div>

            <input
              type="text"
              name="website_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="form-anti-bot-field"
            />

            {status === 'success' && (
              <p className="text-sm text-primary-400 flex items-center gap-2 animate-soft-fade">
                <i className="ri-checkbox-circle-fill" />
                提交成功，感谢你的反馈，我们会尽快处理。
              </p>
            )}
            {status === 'error' && (
              <p className="text-sm text-accent-500 flex items-center gap-2 animate-soft-fade">
                <i className="ri-error-warning-fill" />
                {formError}
              </p>
            )}

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <button
                type="submit"
                disabled={status === 'submitting'}
                className={`h-11 px-7 rounded-md text-sm font-semibold whitespace-nowrap transition-colors inline-flex items-center gap-2 ${
                  status === 'submitting'
                    ? 'bg-background-300 text-foreground-500 cursor-not-allowed'
                    : 'bg-primary-500 text-background-50 hover:bg-primary-600 cursor-pointer'
                }`}
              >
                <i className="ri-send-plane-fill" />
                {status === 'submitting' ? '提交中…' : '提交反馈'}
              </button>
              <p className="text-xs text-foreground-500 leading-relaxed">
                提交即表示你同意我们按照隐私提示使用你所填写的信息。
              </p>
            </div>
          </form>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to="/videos"
              className="h-10 px-5 rounded-md border border-background-300 text-sm text-foreground-800 hover:border-primary-500/60 transition-colors cursor-pointer whitespace-nowrap inline-flex items-center gap-2"
            >
              <i className="ri-film-line" />
              浏览视频资源
            </Link>
            <Link
              to="/search"
              className="h-10 px-5 rounded-md border border-background-300 text-sm text-foreground-800 hover:border-primary-500/60 transition-colors cursor-pointer whitespace-nowrap inline-flex items-center gap-2"
            >
              <i className="ri-search-line" />
              全站搜索
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}